import type {
  AddressableLine,
  SegmentAnalysisView,
  SegmentChartKind,
  SegmentDetailTab,
  SegmentFilters,
  SegmentGridSort,
  SegmentKpiDensity,
} from "../types"

import { SEGMENT_CHART_OPTIONS, SEGMENT_GRID_SORT_OPTIONS } from "../types"

/**
 * Per-user view preferences (§G9) — the USER layer of the system → tenant → user
 * config model. These are a pure view concern (never official data), so they live
 * client-side in localStorage keyed by the user's oid and are layered on top of the
 * tenant-merged config the backend returns. Available to every role.
 */
export type SegmentViewPrefs = {
  /** Preferred landing view (Portfolio / Companies / Map). */
  defaultView?: SegmentAnalysisView
  /** Which portfolio graph the user chose to display (§G9). */
  chart?: SegmentChartKind
  /** Tenant addressable-line keys the user chose to hide from their KPIs/cards/hero. */
  hiddenAddressableKeys?: string[]
  /** KPI card ids the user chose for the portfolio row (#2067); absent → defaults.
   * Order is meaningful (#2068 A1 reorder): the row renders in this order.
   * Stale/unknown ids are dropped at render time, never here, so a tenant config
   * change can restore a card without the user re-ticking it. */
  kpiCards?: string[]
  /** KPI row density (#2068 A3); absent → comfortable. */
  kpiDensity?: SegmentKpiDensity
  /** Portfolio charts to stack (#2068 B1); absent → [chart] or the bubble default. */
  charts?: SegmentChartKind[]
  /** Portfolio grid sort (#2068 B3); absent → own-first (classic). */
  gridSort?: SegmentGridSort
  /** Last-used portfolio filters, restored on load (#2068 C2); absent → empty. */
  stickyFilters?: SegmentFilters
  /** Tab a freshly opened segment detail lands on (#2068 D1); absent → market. */
  defaultDetailTab?: SegmentDetailTab
  /** Stale-segment threshold in days for the KPI card (#2069 A2); absent → 30. */
  staleDays?: 30 | 60 | 90
  /** Hero-card stat ids (#2069 B2); absent → the classic addressable-aware trio. */
  cardStats?: string[]
  /** Detail tabs the user hid (#2069 D2); "market" is never hideable. */
  hiddenDetailTabs?: SegmentDetailTab[]
  /** Dossier sections the user hid (#2069 D2). */
  hiddenDossierSections?: string[]
  /** Named filter presets (#2069 C1); recalled as chips in the filter bar. */
  filterPresets?: Array<{ name: string; filters: SegmentFilters }>
}

export const STALE_DAYS_OPTIONS = [30, 60, 90] as const

/** Hero-card stat catalog (#2069 B2 / hygiene #2421). */
export const CARD_STAT_OPTIONS: Array<{ id: string; label: string }> = [
  { id: "addressable", label: "Addressable market" },
  { id: "cagr", label: "CAGR 5y" },
  { id: "share", label: "Share %" },
  { id: "sales", label: "Sales MEUR" },
]

/** Dossier section ids (#2069 D2) — must match company-dossier's section keys. */
export const DOSSIER_SECTION_OPTIONS: Array<{ id: string; label: string }> = [
  { id: "profile", label: "Profile" },
  { id: "moves", label: "Recent moves" },
  { id: "fit", label: "Fit assessment" },
  { id: "relationship", label: "Relationship & outreach" },
  { id: "claims", label: "Grounded claims" },
]

const MAX_FILTER_PRESETS = 8

// Phase 3 (#2070): the backend is the source of truth for prefs; localStorage is
// ONLY a paint cache of the last backend response (user-approved) so a hard
// reload renders the right cards/charts before the API answers. The legacy
// localStorage-authoritative key is deliberately dropped, not migrated.
const PREFIX = "segment-analysis:view-prefs-cache:"
const LEGACY_PREFIX = "segment-analysis:view-prefs:"
const VIEWS: SegmentAnalysisView[] = ["portfolio", "companies", "map"]
const CHART_KINDS = SEGMENT_CHART_OPTIONS.map((option) => option.id)
const GRID_SORTS = SEGMENT_GRID_SORT_OPTIONS.map((option) => option.id)
const DETAIL_TABS: SegmentDetailTab[] = [
  "market",
  "customers",
  "competitors",
  "swot",
  "actions",
  "history",
]

function storageKey(oid: string | null): string {
  return `${PREFIX}${oid ?? "anon"}`
}

/** Drop anything that isn't a known view / string key so a tampered or stale blob
 * can't break rendering. Returns a minimal, normalised object. */
export function sanitizeViewPrefs(value: unknown): SegmentViewPrefs {
  if (!value || typeof value !== "object") return {}
  const raw = value as Record<string, unknown>
  const out: SegmentViewPrefs = {}
  if (
    typeof raw.defaultView === "string" &&
    VIEWS.includes(raw.defaultView as SegmentAnalysisView)
  ) {
    out.defaultView = raw.defaultView as SegmentAnalysisView
  }
  if (typeof raw.chart === "string" && CHART_KINDS.includes(raw.chart as SegmentChartKind)) {
    out.chart = raw.chart as SegmentChartKind
  }
  if (Array.isArray(raw.hiddenAddressableKeys)) {
    const keys = Array.from(
      new Set(raw.hiddenAddressableKeys.filter((k): k is string => typeof k === "string"))
    )
    if (keys.length) out.hiddenAddressableKeys = keys
  }
  if (Array.isArray(raw.kpiCards)) {
    const ids = Array.from(new Set(raw.kpiCards.filter((k): k is string => typeof k === "string")))
    if (ids.length) out.kpiCards = ids
  }
  if (raw.kpiDensity === "compact") out.kpiDensity = "compact" // comfortable = absent
  if (Array.isArray(raw.charts)) {
    const charts = Array.from(
      new Set(
        raw.charts.filter((c): c is SegmentChartKind => CHART_KINDS.includes(c as SegmentChartKind))
      )
    )
    if (charts.length) out.charts = charts
  }
  if (
    typeof raw.gridSort === "string" &&
    GRID_SORTS.includes(raw.gridSort as SegmentGridSort) &&
    raw.gridSort !== "own-first" // default = absent
  ) {
    out.gridSort = raw.gridSort as SegmentGridSort
  }
  const sticky = sanitizeStickyFilters(raw.stickyFilters)
  if (sticky) out.stickyFilters = sticky
  if (
    typeof raw.defaultDetailTab === "string" &&
    DETAIL_TABS.includes(raw.defaultDetailTab as SegmentDetailTab) &&
    raw.defaultDetailTab !== "market" // default = absent
  ) {
    out.defaultDetailTab = raw.defaultDetailTab as SegmentDetailTab
  }
  if (raw.staleDays === 60 || raw.staleDays === 90) out.staleDays = raw.staleDays // 30 = absent
  if (Array.isArray(raw.cardStats)) {
    const known = new Set(CARD_STAT_OPTIONS.map((option) => option.id))
    const rawIds = raw.cardStats.filter((k): k is string => typeof k === "string")
    // A saved selection referencing an id the catalog no longer has (e.g. a
    // future #2461-style tile cleanup) must not silently narrow to whatever
    // survives — drop the whole selection so the caller falls back to the
    // classic default trio (#3295 Nolato feedback #3), instead of keeping
    // only the survivors.
    const hasStaleId = rawIds.some((k) => !known.has(k))
    if (!hasStaleId) {
      const ids = Array.from(new Set(rawIds)).slice(0, 4)
      if (ids.length) out.cardStats = ids
    }
  }
  if (Array.isArray(raw.hiddenDetailTabs)) {
    const tabs = Array.from(
      new Set(
        raw.hiddenDetailTabs.filter(
          (t): t is SegmentDetailTab =>
            typeof t === "string" && t !== "market" && DETAIL_TABS.includes(t as SegmentDetailTab)
        )
      )
    )
    if (tabs.length) out.hiddenDetailTabs = tabs
  }
  if (Array.isArray(raw.hiddenDossierSections)) {
    const known = new Set(DOSSIER_SECTION_OPTIONS.map((option) => option.id))
    const ids = Array.from(
      new Set(
        raw.hiddenDossierSections.filter((k): k is string => typeof k === "string" && known.has(k))
      )
    )
    if (ids.length) out.hiddenDossierSections = ids
  }
  if (Array.isArray(raw.filterPresets)) {
    const presets: Array<{ name: string; filters: SegmentFilters }> = []
    for (const entry of raw.filterPresets) {
      if (!entry || typeof entry !== "object") continue
      const candidate = entry as Record<string, unknown>
      const name = typeof candidate.name === "string" ? candidate.name.trim().slice(0, 40) : ""
      const filters = sanitizeStickyFilters(candidate.filters)
      if (name && filters && !presets.some((p) => p.name === name)) {
        presets.push({ name, filters })
      }
      if (presets.length >= MAX_FILTER_PRESETS) break
    }
    if (presets.length) out.filterPresets = presets
  }
  return out
}

/** Accept only well-formed, non-empty filter blobs — a tampered value degrades to
 * "no sticky filters" rather than poisoning the reducer. */
function sanitizeStickyFilters(value: unknown): SegmentFilters | undefined {
  if (!value || typeof value !== "object") return undefined
  const raw = value as Record<string, unknown>
  const strings = (input: unknown): string[] =>
    Array.isArray(input) ? input.filter((entry): entry is string => typeof entry === "string") : []
  const filters: SegmentFilters = {
    market: strings(raw.market),
    // Priority chips removed from SegmentFilters entirely (#3295 Nolato feedback
    // #19, following the #2421 UI removal) — any orphaned sticky/preset priority
    // in `raw` is simply ignored below (never read into `filters`).
    owner: strings(raw.owner),
    search: typeof raw.search === "string" ? raw.search : "",
    favouritesOnly: raw.favouritesOnly === true,
  }
  const hasAny =
    filters.market.length > 0 ||
    filters.owner.length > 0 ||
    filters.search !== "" ||
    filters.favouritesOnly
  return hasAny ? filters : undefined
}

export function loadViewPrefs(oid: string | null): SegmentViewPrefs {
  try {
    // Drop the pre-Phase-3 localStorage-authoritative blob (user-confirmed).
    window.localStorage.removeItem(`${LEGACY_PREFIX}${oid ?? "anon"}`)
    const raw = window.localStorage.getItem(storageKey(oid))
    return raw ? sanitizeViewPrefs(JSON.parse(raw)) : {}
  } catch {
    return {}
  }
}

export function saveViewPrefs(oid: string | null, prefs: SegmentViewPrefs): void {
  try {
    const clean = sanitizeViewPrefs(prefs)
    // Empty prefs → remove the key so "reset to defaults" leaves no residue.
    if (Object.keys(clean).length === 0) {
      window.localStorage.removeItem(storageKey(oid))
      return
    }
    window.localStorage.setItem(storageKey(oid), JSON.stringify(clean))
  } catch {
    // Best-effort: view prefs are non-critical, never block the UI on storage errors.
  }
}

/**
 * Resolve the charts to stack (#2068 B1): the multi-select pref wins; otherwise
 * migrate the legacy single `chart` pref; otherwise the classic bubble default.
 */
export function effectiveCharts(prefs: SegmentViewPrefs): SegmentChartKind[] {
  // Hygiene #2421: only bubble remains selectable; stale prefs degrade to bubble.
  const allowed = new Set(SEGMENT_CHART_OPTIONS.map((option) => option.id))
  if (prefs.charts?.length) {
    const kept = prefs.charts.filter((chart) => allowed.has(chart))
    if (kept.length) return kept
  }
  const single =
    prefs.chart && allowed.has(prefs.chart) ? prefs.chart : ("bubble" as SegmentChartKind)
  return [single]
}

/**
 * Apply the user layer to the tenant's addressable lines: hide the keys the user
 * chose to hide. A user can only hide lines the tenant defined — never invent new
 * ones (§G9). With no prefs the tenant lines pass through unchanged.
 */
export function applyAddressableLinePrefs(
  tenantLines: AddressableLine[],
  prefs: SegmentViewPrefs
): AddressableLine[] {
  const hidden = new Set(prefs.hiddenAddressableKeys ?? [])
  return tenantLines.filter((line) => !hidden.has(line.key))
}
